import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PartyPopper, StickyNote, HardHat } from 'lucide-react';
import Badge from '../ui/Badge';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

export default function DayTooltip({ date, jobs, holidayName, note, cellRef }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      // Tooltip width: ~ minWidth 280 + padding. Buffer 320px from right edge.
      const TOOLTIP_W = 320;
      const wouldOverflowRight = (rect.right + 10 + TOOLTIP_W) > window.innerWidth;
      const left = wouldOverflowRight
        ? rect.left + window.scrollX - TOOLTIP_W - 10   // flip to left
        : rect.right + window.scrollX + 10;             // default right
      setPosition({
        top: rect.top + window.scrollY,
        left: left
      });
    }
  }, [cellRef]);

  if (!jobs || jobs.length === 0) return null;

  const tooltipContent = (
    <div style={{
      position: 'absolute',
      top: `${position.top}px`,
      left: `${position.left}px`,
      background: PAPER,
      borderRadius: '12px',
      padding: '12px 16px',
      boxShadow: `0 12px 40px ${INK}4D`,
      border: `2px solid ${WALNUT}`,
      minWidth: '280px', maxWidth: '400px',
      zIndex: 99999, pointerEvents: 'none'
    }}>
      <div style={{
        fontSize: '13px', fontWeight: '600',
        color: WALNUT,
        marginBottom: '8px'
      }}>
        {new Date(date).toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric'
        })}
      </div>

      {holidayName && (
        <div style={{
          fontSize: '12px',
          color: RUST,
          marginBottom: '8px', fontWeight: '500',
          display: 'flex', alignItems: 'center', gap: '5px'
        }}><PartyPopper size={13} strokeWidth={2} /> {holidayName}</div>
      )}

      {note && (
        <div style={{
          fontSize: '12px',
          color: STONE,
          marginBottom: '8px', fontStyle: 'italic',
          padding: '6px 8px',
          background: `${WALNUT}0D`,
          borderRadius: '6px',
          display: 'flex', alignItems: 'center', gap: '5px'
        }}><StickyNote size={13} strokeWidth={2} style={{ flexShrink: 0 }} /> {note}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {jobs.map((job, index) => (
          <div
            key={index}
            style={{
              fontSize: '12px',
              display: 'flex', flexDirection: 'column', gap: '4px',
              padding: '8px',
              background: `${INK}05`,
              borderRadius: '6px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Badge status={job.status || 'scheduled'} style={{ fontSize: '10px', padding: '2px 6px' }}>
                {job.status || 'scheduled'}
              </Badge>
              <span style={{ color: INK, fontWeight: '600' }}>
                {job.contractNumber || 'No Contract #'}
              </span>
            </div>
            <div style={{ color: STONE }}>
              {job.customerName || job.name}
            </div>
            <div style={{ color: STONE, fontSize: '11px' }}>
              {job.name || job.location || job.address || ''}
            </div>
            {job.assignedCrew && (
              <div style={{ color: WALNUT, fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <HardHat size={12} strokeWidth={2} /> {job.assignedCrew}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
}
